import React from 'react';
import { useUser } from '../contexts/UserContext.tsx';

const ProgressDemo: React.FC = () => {
    const {
        userData,
        completeAksara,
        earnAchievement,
        completeCourse,
        addMoney,
        spendMoney,
        addHeart,
        loseHeart,
        addXp,
        getXpProgress,
        purchaseHearts,
        getHeartRecoveryInfo,
        formatHeartRecoveryTime,
        getCurrentStreak
    } = useUser();

    if (!userData) {
        return (
            <div className="p-4 bg-gray-100 rounded-lg">
                <p>No user data available</p>
            </div>
        );
    }

    const handleCompleteAksara = () => {
        // Use a real achievement ID from the data
        const success = completeAksara('lesson-basic-1');
        if (success) {
            alert('Aksara lesson completed! +10 XP');
            addMoney(10); // Reward with money
        }
    };

    const handleEarnAchievement = () => {
        // Use a real achievement ID from achievementData.ts
        const success = earnAchievement(1); // "Login Pertama"
        if (success) {
            alert('Achievement earned: Login Pertama!');
            addMoney(50); // Achievement reward
        }
    };

    const handleCompleteCourse = () => {
        const success = completeCourse('batak-toba');
        if (success) {
            alert('Course completed: Batak Toba!');
            addMoney(100); // Course completion reward
        }
    };

    const handleAddMoney = () => {
        const success = addMoney(25);
        if (success) {
            alert('Earned 25 money!');
        }
    };

    const handleSpendMoney = () => {
        const success = spendMoney(10);
        if (success) {
            alert('Spent 10 money!');
        } else {
            alert('Not enough money!');
        }
    };

    const handleLoseHeart = () => {
        const success = loseHeart(1);
        if (success) {
            alert('Lost 1 heart!');
        }
    };

    const handleGainHeart = () => {
        const success = addHeart(1);
        if (success) {
            alert('Gained 1 heart!');
        }
    };

    const handleAddXp = () => {
        const success = addXp(25);
        if (success) {
            const xpData = getXpProgress();
            alert(`Gained 25 XP! Current level: ${xpData.level}, XP: ${xpData.currentXp}/${xpData.xpToNextLevel}`);
        }
    };

    const handleShowXpInfo = () => {
        const xpData = getXpProgress();
        alert(`Level ${xpData.level}\nCurrent XP: ${xpData.currentXp}/${xpData.xpToNextLevel}\nTotal XP: ${xpData.totalXp}\nProgress: ${xpData.progressPercent.toFixed(1)}%`);
    };

    const handlePurchaseHeart = () => {
        const result = purchaseHearts(1);
        alert(result.message);
    };

    const handleShowHeartInfo = () => {
        const heartInfo = getHeartRecoveryInfo();
        if (heartInfo.isRecovering) {
            const timeStr = formatHeartRecoveryTime(heartInfo.timeUntilNextHeart);
            alert(`Hearts: ${heartInfo.currentHearts}/${heartInfo.maxHearts}\nNext heart in: ${timeStr}`);
        } else {
            alert(`Hearts: ${heartInfo.currentHearts}/${heartInfo.maxHearts}\nHearts full!`);
        }
    };

    return (
        <div className="p-6 bg-white rounded-lg shadow-lg max-w-md mx-auto">
            <h3 className="text-lg font-bold mb-4">Progress Demo</h3>

            {/* User Info */}
            <div className="mb-6">
                <div className="flex items-center mb-2">
                    <img
                        src={userData.profile.photo}
                        alt="Profile"
                        className="w-12 h-12 rounded-full object-cover mr-3"
                    />
                    <div>
                        <p className="font-medium">{userData.profile.username}</p>
                        <p className="text-sm text-gray-600">
                            Streak: {getCurrentStreak()} days
                        </p>
                    </div>
                </div>
            </div>

            {/* Meta Stats */}
            <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="text-center p-3 bg-yellow-50 rounded-lg">
                    <p className="text-2xl font-bold text-yellow-600">
                        {userData.meta.money}
                    </p>
                    <p className="text-xs text-yellow-600">Money</p>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">
                        {userData.meta.heart}
                    </p>
                    <p className="text-xs text-red-600">Hearts</p>
                </div>
            </div>

            {/* XP and Level Stats */}
            <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="text-center p-3 bg-indigo-50 rounded-lg">
                    <p className="text-2xl font-bold text-indigo-600">
                        {userData.meta.level}
                    </p>
                    <p className="text-xs text-indigo-600">Level</p>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">
                        {userData.meta.xp}
                    </p>
                    <p className="text-xs text-purple-600">Total XP</p>
                </div>
            </div>

            {/* Progress Stats */}
            <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">
                        {userData.progress.aksaraCompleted.length}
                    </p>
                    <p className="text-xs text-blue-600">Aksara</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">
                        {userData.progress.achievements.length}
                    </p>
                    <p className="text-xs text-green-600">Achievements</p>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">
                        {userData.progress.coursesCompleted.length}
                    </p>
                    <p className="text-xs text-purple-600">Courses</p>
                </div>
            </div>

            {/* Progress Test Buttons */}
            <div className="space-y-2 mb-4">
                <button
                    onClick={handleCompleteAksara}
                    className="w-full py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                >
                    Complete Lesson (+10 Money)
                </button>
                <button
                    onClick={handleEarnAchievement}
                    className="w-full py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
                >
                    Earn Achievement (+50 Money)
                </button>
                <button
                    onClick={handleCompleteCourse}
                    className="w-full py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm"
                >
                    Complete Course (+100 Money)
                </button>
            </div>

            {/* Meta Test Buttons */}
            <div className="grid grid-cols-2 gap-2 mb-4">
                <button
                    onClick={handleAddMoney}
                    className="py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors text-sm"
                >
                    +25 Money
                </button>
                <button
                    onClick={handleSpendMoney}
                    className="py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm"
                >
                    -10 Money
                </button>
                <button
                    onClick={handleGainHeart}
                    className="py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors text-sm"
                >
                    +1 Heart
                </button>
                <button
                    onClick={handleLoseHeart}
                    className="py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm"
                >
                    -1 Heart
                </button>
                <button
                    onClick={handlePurchaseHeart}
                    className="py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
                >
                    Buy Heart (50)
                </button>
                <button
                    onClick={handleShowHeartInfo}
                    className="py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                >
                    Heart Info
                </button>
                <button
                    onClick={handleAddXp}
                    className="py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors text-sm"
                >
                    +25 XP
                </button>
                <button
                    onClick={handleShowXpInfo}
                    className="py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                >
                    XP Info
                </button>
            </div>

            {/* Recent Items */}
            <div className="mt-6 text-xs">
                <p className="font-medium mb-2">Recent Progress:</p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                    {[
                        ...userData.progress.aksaraCompleted.slice(-2).map(id => ({ id, type: 'Aksara', color: 'text-blue-600' })),
                        ...userData.progress.achievements.slice(-2).map(id => ({ id, type: 'Achievement', color: 'text-green-600' })),
                        ...userData.progress.coursesCompleted.slice(-2).map(id => ({ id, type: 'Course', color: 'text-purple-600' })),
                    ].slice(-4).map((item, index) => (
                        <p key={index} className={`${item.color} text-xs`}>
                            {item.type}: {item.id}
                        </p>
                    ))}
                    {userData.progress.aksaraCompleted.length === 0 &&
                        userData.progress.achievements.length === 0 &&
                        userData.progress.coursesCompleted.length === 0 && (
                            <p className="text-gray-400">No progress yet</p>
                        )}
                </div>
            </div>

            {/* Streak Info */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium">Login Streak</p>
                <p className="text-xs text-gray-600">
                    Current: {userData.meta.streak.current} days
                </p>
                <p className="text-xs text-gray-600">
                    Last login: {userData.meta.streak.lastLoginDate}
                </p>
            </div>
        </div>
    );
};

export default ProgressDemo;
